import { useMemo, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { QuestionTypes, type OtherAnswer, type SurveyQuestion as Question, type QuestionAnswer } from "../../../types";

import { ChoiseAnswers } from "./Answers/ChoiseAnswers";
import { FreeText } from "./Answers/FreeText";
import { RatingScale } from "./Answers/RatingScale";
import styles from "./SurveyQuestion.module.scss";

interface Props {
  question: Question;
  setSelectedAnswers: (answers: QuestionAnswer[]) => void;
  selectedAnswers: QuestionAnswer[];
  otherText: OtherAnswer;
  setOtherText: React.Dispatch<SetStateAction<OtherAnswer>>;
  questionOrder: number;
}

export const SurveyQuestion: React.FC<Props> = ({
  question,
  setSelectedAnswers,
  selectedAnswers,
  otherText,
  setOtherText,
  questionOrder,
}) => {
  const { t } = useTranslation("assets");

  const isQuestionMultiselect = useMemo(() => question.type === QuestionTypes.MultipleChoice, [question]);

  const getBody = () => {
    switch (question.type) {
      case QuestionTypes.MultipleChoice:
      case QuestionTypes.SingleChoice:
        return (
          <ChoiseAnswers
            question={question}
            selectedAnswers={selectedAnswers}
            setSelectedAnswers={setSelectedAnswers}
            otherAnswer={otherText}
            setOtherAnswer={setOtherText}
          />
        );
      case QuestionTypes.FreeText:
        return (
          <FreeText question={question} selectedAnswers={selectedAnswers} setSelectedAnswers={setSelectedAnswers} />
        );
      case QuestionTypes.RatingScale:
        return (
          <RatingScale question={question} selectedAnswers={selectedAnswers} setSelectedAnswers={setSelectedAnswers} />
        );
    }
  };

  const questionText = question.type === QuestionTypes.RatingScale ? question.question.text : question.question;

  return (
    <div className={styles.root}>
      <div
        className={styles.questionTitle}
        tabIndex={0}
        aria-label={questionText}
      >{`${questionOrder}. ${questionText} ${isQuestionMultiselect ? t("assessments.selectAllThatApply") : ""}`}</div>
      {getBody()}
    </div>
  );
};
